import {
  EffectComposer,
  Outline,
  Vignette,
  ChromaticAberration,
  SMAA,
  Bloom,
  HueSaturation,
  BrightnessContrast,
  Noise,
  Scanline,
  GodRays,
  TiltShift2,
  Sepia,
} from "@react-three/postprocessing";
import { BlendFunction } from "postprocessing";
import { useAudioStore } from "./stores/useAudioStore";

export default function ComicPostProcessing() {
  const { currentMood } = useAudioStore();

  // Cappuccino - Style de base, doux et neutre
  if (currentMood.name === "Cappuccino") {
    return (
      <EffectComposer multisampling={0}>
        <SMAA />

        <HueSaturation hue={0.0} saturation={-0.05} />
        <BrightnessContrast brightness={0.02} contrast={0.08} />
      </EffectComposer>
    );
  }

  // Americano - Style noir et blanc vintage avec bruit
  if (currentMood.name === "Americano") {
    return (
      <EffectComposer multisampling={0}>
        <SMAA />
        {/* Noir et blanc */}
        <HueSaturation hue={1} saturation={-1.0} />
        {/* Augmenter le contraste pour un effet plus marqué */}
        <BrightnessContrast contrast={0.1} />
        {/* Aberration chromatique pour effet vintage */}
        <ChromaticAberration
          offset={[0.001, 0.001]}
          blendFunction={BlendFunction.NORMAL}
        />
        {/* Bruit de film vintage */}
        <Noise opacity={1} blendFunction={BlendFunction.OVERLAY} />
        {/* Lignes de scan optionnelles pour effet TV */}
        <Scanline density={1.5} opacity={0.2} />
      </EffectComposer>
    );
  }

  // Latte Coco - Style rêveur avec bloom
  if (currentMood.name === "Latte Coco") {
    return (
      <EffectComposer multisampling={0}>
        <SMAA />
        <Outline
          selection={null}
          visibleEdgeColor={0xffffff}
          hiddenEdgeColor={0xaaaaaa}
          edgeStrength={0.6}
          pulseSpeed={0}
        />
        {/* Désature globalement l'image pour un rendu très pâle */}
        <HueSaturation hue={0} saturation={-0.6} />
        {/* Accentue la luminosité et contraste faiblement pour effet "papier blanc" */}
        <BrightnessContrast brightness={0.3} contrast={-0.05} />
      </EffectComposer>
    );
  }

  // Milkshake - Style rose pastel dreamy
  if (currentMood.name === "Milkshake") {
    return (
      <EffectComposer multisampling={0}>
        <SMAA />
        {/* Teinte rose/magenta */}
        <HueSaturation hue={-0.05} saturation={0.3} />
        {/* Luminosité augmentée pour effet pastel doux */}
        <BrightnessContrast brightness={0.15} contrast={-0.1} />
        {/* Aberration chromatique subtile pour douceur */}
        <ChromaticAberration
          offset={[0.0004, 0.0008]}
          blendFunction={BlendFunction.SOFT_LIGHT}
        />
      </EffectComposer>
    );
  }

  // Sofa - Style relaxant, minimaliste
  if (currentMood.name === "Coffee Liqueur") {
    return (
      <EffectComposer multisampling={0}>
        <SMAA />
        <Outline
          selection={null}
          visibleEdgeColor={0x225d2d} // vert foncé : edges verdâtres pour effet malade
          hiddenEdgeColor={0x1a3220}
          edgeStrength={75}
          pulseSpeed={1}
        />
        {/* Légère teinte verte et désaturation */}
        <HueSaturation hue={0.1} saturation={-0.27} />
        {/* Baisse légère de contraste pour un aspect terne */}
        <BrightnessContrast brightness={-0.03} contrast={-0.08} />
        {/* Aberration plus forte pour sensation désagréable */}
        <ChromaticAberration
          offset={[0.0012, 0.0022]}
          blendFunction={BlendFunction.NORMAL}
        />
        {/* Léger vignettage */}
        <Vignette
          eskil={false}
          offset={0.5}
          darkness={0.8}
          blendFunction={BlendFunction.NORMAL}
        />
        {/* Léger effet de rotation de l'écran */}
        <Scanline density={1.5} opacity={0.35} />
        {/* On peut simuler l'ivresse/étourdissement avec un effet "TiltShift": flou radial léger */}
        <TiltShift2 blur={0.2} anamorph={0} />
      </EffectComposer>
    );
  }

  // Fallback par défaut
  return (
    <EffectComposer multisampling={0}>
      <SMAA />
    </EffectComposer>
  );
}
